import { formatCents } from "./live";

/** What a share button hands on: an absolute link on the public site, and the words that go with it. */
export interface ShareTarget {
  readonly url: string;
  readonly title: string;
  readonly text?: string;
}

/**
 * Where the public site (hobbyshop.ai) shows a shop, a stream, a break and a
 * card show. A merchant shares these, never the storefront: they are the pages
 * with a link preview, and the ones the HobbyScan app opens.
 */
export class ShareLinks {
  readonly site: string;

  constructor(site: string | null | undefined) {
    this.site = (site?.trim() || "https://hobbyshop.ai").replace(/\/+$/, "");
  }

  /** A shop with no handle yet has no page to share. */
  shop(store: { slug: string | null; name: string | null }): ShareTarget | null {
    if (!store.slug) return null;
    const name = store.name ?? "Our shop";
    return { url: `${this.site}/shop/${encodeURIComponent(store.slug)}`, title: name, text: `${name} on HobbyShop` };
  }

  stream(stream: { id: string; title: string; status: string }, shopName: string | null): ShareTarget {
    const shop = shopName ?? "We";
    const text =
      stream.status === "LIVE"
        ? `${shop} ${shopName ? "is" : "are"} live now. Grab a spot.`
        : stream.status === "ENDED"
          ? "Watch the replay."
          : `Live breaks with ${shopName ?? "us"}. Spots are on sale before it starts.`;
    return { url: `${this.site}/live/${encodeURIComponent(stream.id)}`, title: stream.title, text };
  }

  break(b: {
    id: string;
    title: string;
    status: string;
    soldCount: number;
    slotCount: number;
    defaultPriceCents: number;
  }): ShareTarget {
    const left = Math.max(b.slotCount - b.soldCount, 0);
    const text =
      b.status === "ON_SALE" && left > 0
        ? `${left} of ${b.slotCount} spots left · ${formatCents(b.defaultPriceCents)} a spot`
        : b.status === "COMPLETED"
          ? "See who got what."
          : undefined;
    return { url: `${this.site}/live/breaks/${encodeURIComponent(b.id)}`, title: b.title, text };
  }

  show(event: { id: string; title: string }, when: string): ShareTarget {
    return { url: `${this.site}/shows/${encodeURIComponent(event.id)}`, title: event.title, text: when };
  }
}

const message = (target: ShareTarget) => (target.text ? `${target.text}\n${target.url}` : target.url);

export const emailHref = (target: ShareTarget): string =>
  `mailto:?subject=${encodeURIComponent(target.title)}&body=${encodeURIComponent(message(target))}`;

/** `sms:?&body=` is the one form both iOS and Android Messages read the body from. */
export const messageHref = (target: ShareTarget): string => `sms:?&body=${encodeURIComponent(message(target))}`;

/** The link as a person reads it: no scheme, no trailing slash. */
export const displayUrl = (url: string): string => url.replace(/^https?:\/\//, "").replace(/\/$/, "");
